/**
 * Tenant-facing `/v1/notifications` wire contract (#1396 phase 1).
 *
 * THE FINGERPRINT IS THE ONLY IRREVERSIBLE POINT (#1396, confirmed with the
 * desktop side 2026-07-28):
 *   1. The server issues it, as a response field.
 *   2. Clients never parse its structure — string equality only.
 *   3. It carries a mandatory `v1:` version prefix; a future format ships as
 *      `v2:` and old clients treat unknown prefixes as distinct events.
 * Client-local rules use a `local:` prefix the server never produces or
 * consumes — the two namespaces cannot collide.
 *
 * SYNC KEY IS last_seen_at, NOT created_at (#1396): a dedup write advances
 * only `last_seen_at`, so an event that keeps recurring while a client is
 * offline resurfaces on reconnect. `since` filters AND sorts on it; the
 * client's next cursor is the max `lastSeenAt` it has seen.
 */

export type NotificationKind =
  | 'balance'
  | 'budget'
  | 'anomaly'
  | 'status'
  | 'ticket'
  | 'system'
  | 'promoter';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export const NOTIFICATION_KINDS: readonly NotificationKind[] = [
  'balance',
  'budget',
  'anomaly',
  'status',
  'ticket',
  'system',
  'promoter',
];

export const NOTIFICATION_SEVERITIES: readonly NotificationSeverity[] = [
  'info',
  'warning',
  'critical',
];

/** Mandatory server-side fingerprint prefix. */
export const NOTIFICATION_FINGERPRINT_PREFIX = 'v1:';

/** Retention window: rows whose last_seen_at is older are purged (#1396 C3). */
export const NOTIFICATION_RETENTION_DAYS = 90;

export interface NotificationView {
  readonly id: string;
  readonly fingerprint: string;
  readonly kind: NotificationKind;
  readonly severity: NotificationSeverity;
  readonly title: string;
  readonly description: string;
  readonly createdAt: string;
  /** The sync cursor: pass the max seen value back as `since`. */
  readonly lastSeenAt: string;
}

export interface ListNotificationsResponse {
  readonly notifications: readonly NotificationView[];
}
